import React, { FC, HTMLAttributes, useState, useReducer } from 'react';
import { makeStyles, withStyles } from 'tss-react/mui';
import {
  Button,
  TableContainer,
  Paper,
  Table,
  TableHead,
  TableRow,
  TableBody,
  TableCell,
  TextField,
  IconButton,
} from '@mui/material';
import { IncentiveAward, IncentiveRequirementSet, IncentiveFieldType, AwardOperatorType } from '../../../types/incentive-types';
import { useCommonStyles } from '../../../../../common/styles/common-styles';
import AddIcon from '@mui/icons-material/Add';
import HighlightOffIcon from '@mui/icons-material/HighlightOff';
import AwardRequirementCard from './AwardRequirementCard';
import ArrowDropUpIcon from '@mui/icons-material/ArrowDropUp';
import ArrowDropDownIcon from '@mui/icons-material/ArrowDropDown';
import { numberSeparator, removeSeparator, useUpdateEffect } from 'src/app/common/utils';

type IncentiveAwardTableProps = {
  isDisabled: boolean;
  incentiveAward?: IncentiveAward[];
  onUpdate: (incentiveAward: IncentiveAward[]) => void;
};

const useStyles = makeStyles()((theme) => ({
  popupWrapper: {
    position: 'relative',
    display: 'inline-block'
  },
  cardPos: {
    position: 'absolute',
    left: '100%',
    transform: 'translate(5px, -50%)',
    zIndex: 1
  },
  rowContainer: {
    display: 'flex',
    alignItems: 'center'
  },
  subHeader: {
    fontSize: '1rem',
    lineHeight: 1.2
  },
  inputBox: {
    paddingTop: 5,
    paddingBottom: 8
  }
}));

const IncentiveTableCell = withStyles(TableCell, (theme) => ({
  head: {
    backgroundColor: theme.palette.common.black,
    color: theme.palette.common.white,
    fontSize: '1.1rem',
    fontWeight: 'bold'
  },
  body: {
    fontWeight: 'bold',
    paddingTop: 8,
    paddingBottom: 8
  }
}));

type IncentiveRequirementInternal = {
  setId: string;
  operand: string;
  operator: AwardOperatorType;
  type: IncentiveFieldType;
  values: string;
}

type IncentiveAwardInternal = {
  name: string;
  tier: number;
  requirements: IncentiveRequirementInternal[];
}

type IncentiveAwardState = {
  tierAwards: IncentiveAwardInternal[];
};

type UpdateAwardColumnAction = {
  type: "UPDATE_AWARD_COLUMN";
  payload: {
    requirementSet: IncentiveRequirementSet[];
  };
}

type AddAwardRowAction = {
  type: "ADD_AWARD_ROW";
  payload: {
    requirementSet: IncentiveRequirementSet[];
  };
};

type ResetAwardsAction = {
  type: "RESET_AWARDS";
};

type MoveAwardRowAction = {
  type: 'MOVE_AWARD_ROW';
  payload: {
    from: number;
    to: number;
  };
};

type ModifyRowFieldAction = {
  type: "MODIFY_ROW_FIELD";
  payload: {
    index: number;
    field: keyof Pick<IncentiveAwardInternal, 'name'>;
    value: string;
  };
};

type DeleteAwardRowAction = {
  type: "DELETE_AWARD_ROW";
  payload: {
    index: number;
  }
}

type ModifyRequirementItemAction = {
  type: "MODIFY_ROW_REQUIREMENT_ITEM";
  payload: {
    index: number;
    setId: string;
    value: string;
  }
}
type IncentiveAwardAction = AddAwardRowAction | UpdateAwardColumnAction | MoveAwardRowAction | ResetAwardsAction |
                            ModifyRowFieldAction | ModifyRequirementItemAction | DeleteAwardRowAction;
const incentiveAwardReducer = (state: IncentiveAwardState, action: IncentiveAwardAction): IncentiveAwardState => {
  switch (action.type) {
    case 'ADD_AWARD_ROW':
      return {
        tierAwards: [...state.tierAwards, {
          name: '',
          tier: state.tierAwards.length + 1,
          requirements: action.payload.requirementSet.map((requirementItem, index) => ({
            setId: String(index),
            operand: requirementItem.operand,
            operator: requirementItem.operator as AwardOperatorType,
            type: requirementItem.type as IncentiveFieldType,
            values: ''
          }))
        }]
      };
    case 'MODIFY_ROW_FIELD':
      let item = state.tierAwards[action.payload.index];
      item[action.payload.field] = action.payload.value;
      return {
        ...state
      };
    case 'MOVE_AWARD_ROW':
      let tierAwards = state.tierAwards;
      tierAwards.splice(action.payload.to, 0, tierAwards.splice(action.payload.from, 1)[0]);
      return {
        tierAwards: tierAwards.map((award, index) => ({
          ...award,
          tier: index + 1
        }))
      };
    case 'UPDATE_AWARD_COLUMN':
      const newTierAwards = state.tierAwards.map(item => {
        let sets = action.payload.requirementSet;
        const newSets: IncentiveRequirementSet[] = [];
        sets.forEach(set => {
          let foundIndex = item.requirements.findIndex(requirement => requirement.setId === set.setId);
          if (foundIndex === -1) {
            newSets.push(set);
          }
          //PHKL Custom - s
          else {
            const originItem = item.requirements[foundIndex];
            item.requirements[foundIndex] = {
              ...originItem,
              operand: set.operand,
              operator: set.operator as AwardOperatorType,
              type: set.type as IncentiveFieldType,
            };
          //PHKL Custom - e
          }
        });
        const keepItems = item.requirements.filter(requirement => sets.find(set => set.setId === requirement.setId));
        return {
          ...item,
          requirements: [
            ...keepItems,
            ...newSets.map(set => ({
              setId: set.setId,
              operand: set.operand,
              operator: set.operator as AwardOperatorType,
              type: set.type as IncentiveFieldType,
              values: ''
            }))
          ]
        };
      });
      return {
        tierAwards: newTierAwards
      };
    case 'MODIFY_ROW_REQUIREMENT_ITEM':
      let rowItem = state.tierAwards[action.payload.index];
      let foundItem = rowItem.requirements.find(item => item.setId === action.payload.setId);
      if (foundItem) {
        foundItem.values = action.payload.value;
      }
      return {
        ...state
      };
    case 'DELETE_AWARD_ROW':
      let awards = state.tierAwards;
      awards.splice(action.payload.index, 1);
      return {
        tierAwards: awards.map((item, index) => ({
          ...item,
          tier: index + 1
        }))
      };
    case 'RESET_AWARDS':
      return {
        tierAwards: []
      }
    default:
      return {
        ...state
      };
  }
}

const IncentiveAwardTable: FC<IncentiveAwardTableProps & HTMLAttributes<HTMLDivElement>> = ({
  isDisabled,
  incentiveAward,
  onUpdate,
  ...rest
}) => {

  const { classes } = useStyles();
  const { classes: commonClasses } = useCommonStyles();

  const [isOpen, setIsOpen] = useState<boolean>(false);

  const requirementSetExtractor = () => {
    if (incentiveAward && incentiveAward.length > 0) {
      const firstItem = incentiveAward[0];
      return firstItem.requirements.map((requirement, index) => ({
        setId: String(index),
        operand: requirement.operand,
        operator: requirement.operator,
        type: requirement.type
      }));
    } else {
      return undefined;
    }
  }
  const [requirementSet, setRequirementSet] = useState<IncentiveRequirementSet[] | undefined>(
    requirementSetExtractor()
  );

  const awardToInternalConvertor = (): IncentiveAwardState => {
    if (incentiveAward && incentiveAward.length > 0) {
      let initialTierAwards = incentiveAward.map(award => {
        let name = award.properties.find(property => property.key === 'name');
        let tier = award.properties.find(property => property.key === 'tier');
        let requirements = award.requirements.map((requirement, index) => ({
          setId: String(index),
          operand: requirement.operand,
          operator: requirement.operator,
          type: requirement.type,
          values: requirement.values[0] ? String(numberSeparator(String(requirement.values[0]))) : ''
        }));
        return {
          name: name ? name.value : '',
          tier: tier ? Number(tier.value) : 0,
          requirements
        };
      });
      return {
        tierAwards: initialTierAwards
      }
    } else {
      return {
        tierAwards: []
      }
    }
  }
  const [awardState, awardDispatch] = useReducer(incentiveAwardReducer, awardToInternalConvertor());

  useUpdateEffect(() => {
    onUpdate(awardState.tierAwards.map(tierAward => ({
      requirements: tierAward.requirements.map(requirement => ({
        operand: requirement.operand,
        operator: requirement.operator,
        type: requirement.type,
        values: [Number(removeSeparator(requirement.values))]
      })),
      properties: [
        {
          key: 'name',
          value: tierAward.name
        },
        {
          key: 'tier',
          value: String(tierAward.tier)
        }
      ]
    })));
  }, [awardState]);

  return (
    <div {...rest}>
      <div style={{ marginBottom: 15 }} className={classes.rowContainer}>
        <div className={classes.popupWrapper}>
          <Button
            disabled={isDisabled}
            variant="contained"
            color="secondary"
            onClick={() => setIsOpen(true)}
          >
            Modify Requirement
          </Button>
          {isOpen &&
            <div className={classes.cardPos}>
              <div className={commonClasses.popupCard}>
                <AwardRequirementCard
                  initialSet={requirementSet}
                  onCancel={() => setIsOpen(false)}
                  onConfirm={(result) => {
                    awardDispatch({ type: 'UPDATE_AWARD_COLUMN', payload: { requirementSet: result || [] }});
                    setRequirementSet(result);
                    if (!result || (result && result.length === 0)) {
                      awardDispatch({ type: 'RESET_AWARDS' });
                    }
                    setIsOpen(false);
                  }}
                />
              </div>
            </div>
          }
        </div>
        <Button
          disabled={isDisabled || !requirementSet || (requirementSet && requirementSet.length <= 0)}
          style={{ marginLeft: 25 }}
          variant="contained"
          color="secondary"
          onClick={() => requirementSet && awardDispatch({ type: 'ADD_AWARD_ROW', payload: { requirementSet }})}
        >
          Add Award
        </Button>
      </div>
      {requirementSet && awardState.tierAwards.length > 0 && (
        <TableContainer component={Paper} style={{ tableLayout: 'fixed' }}>
          <Table>
            <TableHead>
              <TableRow>
                <IncentiveTableCell colSpan={2}>Award</IncentiveTableCell>
                <IncentiveTableCell colSpan={requirementSet.length}>Conditions</IncentiveTableCell>
                <IncentiveTableCell colSpan={1} align="right">
                  <IconButton
                    disabled={isDisabled}
                    style={{ padding: 5, color: 'white' }}
                    onClick={() => awardDispatch({ type: 'ADD_AWARD_ROW', payload: { requirementSet }})}
                  >
                    <AddIcon />
                  </IconButton>
                </IncentiveTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              <TableRow>
                <IncentiveTableCell>Tier</IncentiveTableCell>
                <IncentiveTableCell>Name</IncentiveTableCell>
                {requirementSet.map((requirementItem, index) => (
                  <IncentiveTableCell key={`header-cell-${index}`}>{`${requirementItem.operand} (${requirementItem.operator})`}</IncentiveTableCell>
                ))}
                <IncentiveTableCell/>
              </TableRow>
              {awardState.tierAwards.map((awardItem, awardIndex) => (
                <TableRow key={`award-item-${awardIndex}`}>
                  <IncentiveTableCell>{awardItem.tier}</IncentiveTableCell>
                  <IncentiveTableCell>
                    <TextField
                      disabled={isDisabled}
                      style={{ margin: 0 }}
                      InputProps={{
                        classes: {
                          input: classes.inputBox,
                        },
                      }}
                      margin="dense"
                      variant="outlined"
                      value={awardItem.name}
                      onChange={e => awardDispatch({
                        type: 'MODIFY_ROW_FIELD',
                        payload: {
                          index: awardIndex,
                          field: 'name',
                          value: e.target.value
                        }
                      })}
                    />
                  </IncentiveTableCell>
                  {requirementSet.map((item, index) => {
                    const requirementItem = awardItem.requirements.find(
                      (requirement) => requirement.setId === item.setId,
                    );
                    return (
                      requirementItem && (
                        <IncentiveTableCell key={`requirement-item-children-${index}`}>
                          <TextField
                            disabled={isDisabled}
                            style={{ margin: 0 }}
                            InputProps={{
                              classes: {
                                input: classes.inputBox,
                              },
                            }}
                            margin="dense"
                            variant="outlined"
                            value={requirementItem.values}
                            onChange={(e) => {
                              let onlyNums = e.target.value.replace(/[^0-9]/g, '');
                              if (
                                requirementItem.type === IncentiveFieldType.NUMBER ||
                                requirementItem.type === IncentiveFieldType.CURRENCY
                              ) {
                                onlyNums = numberSeparator(onlyNums);
                              }
                              awardDispatch({
                                type: 'MODIFY_ROW_REQUIREMENT_ITEM',
                                payload: {
                                  index: awardIndex,
                                  setId: requirementItem.setId,
                                  value: onlyNums,
                                },
                              });
                            }}
                          />
                        </IncentiveTableCell>
                      )
                    );
                  })}
                  <IncentiveTableCell align="right">
                    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'flex-end' }}>
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() =>
                          awardState.tierAwards[awardIndex + 1] &&
                          awardDispatch({ type: 'MOVE_AWARD_ROW', payload: { from: awardIndex, to: awardIndex + 1 } })
                        }
                        disabled={isDisabled || !!!awardState.tierAwards[awardIndex + 1]}
                      >
                        <ArrowDropDownIcon />
                      </IconButton>
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => awardState.tierAwards[awardIndex - 1] && awardDispatch({ type: 'MOVE_AWARD_ROW', payload: { from: awardIndex, to: awardIndex - 1}})}
                        disabled={isDisabled || !!!awardState.tierAwards[awardIndex - 1]}
                      >
                        <ArrowDropUpIcon />
                      </IconButton>
                      <IconButton
                        style={{ padding: 5 }}
                        onClick={() => awardDispatch({ type: 'DELETE_AWARD_ROW', payload: { index: awardIndex }})}
                        disabled={isDisabled}
                      >
                        <HighlightOffIcon />
                      </IconButton>
                    </div>
                  </IncentiveTableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        </TableContainer>
      )}
    </div>
  );
};

export default IncentiveAwardTable;
